/**
 * The motion vocabulary.
 *
 * In a 2D renderer there is no perspective to sell weight for you. Everything
 * that makes a card feel like a physical object — mass, thickness, the sense
 * that it left the table — is manufactured out of timing, scale, rotation and
 * shadow. So the timing has to be a *system*, not a pile of ad-hoc numbers.
 *
 * This module is that system. Every duration is derived from `tokens.motion`,
 * every lift from `tokens.elevation` run backwards through `SHADOW` in
 * `layout.ts`, every spatial amplitude from `CARD`. Nothing else in the app is
 * allowed to invent a duration or an ease — import from here.
 *
 * Three filters sit between the vocabulary and the animation, and every value
 * that reaches gsap should pass through one of them:
 *
 *   dur(x)  — seconds, halved under prefers-reduced-motion
 *   amp(x)  — a flourish amplitude, zeroed under prefers-reduced-motion
 *   ease(x) — an ease name, collapsed to something calm under reduced motion
 *
 * That is the whole reduced-motion policy: positional changes survive (the
 * player still needs to see where the card went), durations halve, and the
 * decorative overshoot/wobble/shake amplitude goes to zero.
 */

import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { elevation, motion } from '@/core/tokens';
import { CARD, SHADOW } from '@/pixi/layout';

gsap.registerPlugin(CustomEase);

/* ------------------------------------------------------------------ *
 * Bespoke curves
 * ------------------------------------------------------------------ */

/**
 * The card throw. A hair of anticipation below zero, a hard committed middle,
 * then a whisper of overshoot that the settle picks up. This is the curve that
 * makes a card feel thrown rather than lerped.
 */
export const THROW_EASE = 'w21-throw';
CustomEase.create(THROW_EASE, 'M0,0 C0.04,-0.07 0.16,0.34 0.34,0.68 0.5,0.95 0.66,1.05 0.8,1.02 0.9,1.0 0.95,1 1,1');

/**
 * Weight falling. Reluctant start, real acceleration, abrupt arrival — used
 * for the bust drop, where the card should feel like it was let go of rather
 * than placed.
 */
export const HEAVY_EASE = 'w21-heavy';
CustomEase.create(HEAVY_EASE, 'M0,0 C0.24,0.02 0.44,0.06 0.62,0.28 0.78,0.48 0.88,0.86 1,1');

/**
 * Magnetic snap. Almost all of the distance is covered in the first 15% with a
 * long quiet tail, which reads as "these were pulled together", not "these
 * moved". Used when a resolved pile collapses into one stack.
 */
export const SNAP_EASE = 'w21-snap';
CustomEase.create(SNAP_EASE, 'M0,0 C0.03,0.5 0.08,0.86 0.18,0.95 0.36,1.03 0.66,1.01 1,1');

/* ------------------------------------------------------------------ *
 * Rhythm
 * ------------------------------------------------------------------ */

/**
 * The base rhythmic unit — one sixth of a deal (~70ms). Everything shorter
 * than a named token duration is expressed as a multiple of this, so the whole
 * app beats to the same clock and retuning `motion.deal` retunes the feel.
 */
export const TICK = motion.deal / 6;

/**
 * Hard ceiling for anything the player is waiting on. The round is 180
 * seconds; a sluggish animation is not a style choice, it costs real points.
 * Awaitable methods on the Choreographer resolve within this even when the
 * decorative tail keeps playing.
 */
export const CRITICAL_PATH_MAX = 0.6;

export const DUR = {
  /** Card leaving the stock and arriving on a pile. */
  travel: motion.deal,
  /** Card face turn. */
  flip: motion.flip,
  /** Springy arrival. Runs past the critical path on purpose — it is a tail. */
  settle: motion.settle.duration,
  /** A pile clearing after a bonus. */
  clear: motion.clear,
  /** A bust. Heavier, and allowed to be. */
  bust: motion.bust,
  /** UI fades. */
  ui: motion.ui,

  /** Wind-up before a card commits to leaving. Tiny, or the game feels laggy. */
  anticipate: TICK,
  /** A held beat of stillness. Silence is part of the rhythm. */
  beat: TICK * 1.7,
  /**
   * Span of the ripple that runs through the arriving deck — the *whole* run,
   * not the per-card offset. Fixed span rather than fixed step, so the block
   * stays tight whatever `DEAL_VISIBLE` is set to.
   *
   * Deliberately about a tenth of `dealSweep`: any longer and the leading cards
   * out-run the trailing ones by more than a card, and what was meant to be a
   * deck arriving becomes a queue arriving.
   */
  dealRipple: TICK * 0.28,
  /** The opening block sliding on from off the table. */
  dealSweep: TICK * 2.5,
  /** The two halves of the deck parting before a riffle. */
  riffleSplit: TICK * 1.4,
  /** Per-card offset in the riffle interleave. Mechanical, not floaty. */
  riffleStep: TICK * 0.32,
  /** One card snapping down out of a riffle onto the stack. */
  riffleSnap: TICK * 1.25,
  /** One tap of the dealer squaring the deck. There are two. */
  square: TICK * 2.1,
  /** Per-card offset in a riffle or a cascade. */
  cascade: TICK * 0.85,
  /** Per-card offset when a resolved pile sweeps away. */
  exitStagger: TICK * 0.42,
  /** Hover state transition on a pile well. */
  hover: TICK * 2,
  /** Press response. Must be under one frame budget of perceived delay. */
  press: TICK * 1.2,
  /** Rejection shudder. */
  reject: TICK * 3.2,
  /** Shortest screen shake — an impact. */
  shakeMin: TICK * 2,
  /** Longest screen shake. Beyond this it stops reading as impact and starts
   *  reading as a broken renderer. */
  shakeMax: TICK * 4.3,
  /** The wild's reveal flare. */
  flare: TICK * 3.4,
} as const;

/* ------------------------------------------------------------------ *
 * Eases
 * ------------------------------------------------------------------ */

export const EASE = {
  /** Default for anything crossing the table. */
  travel: motion.travel,
  /** Anything leaving the scene — accelerate out, never ease out. */
  exit: motion.exit,
  /** Springy arrival on the felt. */
  settle: motion.settle.ease,
  /** The card throw. */
  throw: THROW_EASE,
  /** Falling under gravity. */
  heavy: HEAVY_EASE,
  /** Magnetic collapse. */
  snap: SNAP_EASE,
  /** Wind-up before a throw — moves the wrong way first. */
  anticipate: 'back.in(2.4)',
  /** Arrival with a visible overshoot. */
  overshoot: 'back.out(2.6)',
  /** A pop that has to be felt more than seen. */
  pop: 'back.out(3.4)',
  /** Rising off the felt. */
  rise: 'power2.out',
  /** Coming back down. */
  fall: 'power2.in',
  /** Calm, non-expressive fallback. Reduced motion collapses to this. */
  calm: 'power2.out',
  /** Linear, for shake decay envelopes and progress proxies. */
  none: 'none',
  /** Breathing / idle loops. */
  breathe: 'sine.inOut',
} as const;

export type EaseName = string;

/** Eases whose whole point is decorative overshoot — dropped under reduced motion. */
const EXPRESSIVE = /elastic|bounce|back\.|w21-throw|w21-heavy/;

/* ------------------------------------------------------------------ *
 * Depth — the lift ramp
 * ------------------------------------------------------------------ */

/**
 * Virtual lift is the single number that makes 2D read as 3D: the card shader
 * derives its specular parallax from it and the drop shadow derives offset,
 * blur and alpha from it via `SHADOW`. It is *not* a screen offset — a lifted
 * card does not move up the screen, its shadow moves away from it.
 *
 * The elevation ramp in tokens is expressed as shadow blur, so the canonical
 * lift values are that ramp run backwards through the shadow model. Change
 * `elevation` or `SHADOW` and the choreography follows automatically, which is
 * exactly the agreement the lighting model demands.
 */
export function blurForLift(lift: number): number {
  return SHADOW.blurBase + Math.max(0, lift) * SHADOW.blurPerLift;
}

export function liftForBlur(blur: number): number {
  return Math.max(0, (blur - SHADOW.blurBase) / SHADOW.blurPerLift);
}

const REST_LIFT = liftForBlur(elevation.resting.blur);
const LIFTED_LIFT = liftForBlur(elevation.lifted.blur);

export const LIFT = {
  /** Flat on the felt. */
  flat: 0,
  /** Lying in a pile — a card still has thickness. */
  rest: REST_LIFT,
  /** Hovered / pressed / picked at. */
  hover: (REST_LIFT + LIFTED_LIFT) / 2,
  /** Wound up, about to be thrown. */
  ready: LIFTED_LIFT * 0.62,
  /** Mid-flight. The shadow is a long way from the card here — this is the
   *  frame that sells the whole illusion. */
  flight: LIFTED_LIFT,
  /** A resolved pile held up for the player to read. */
  hero: LIFTED_LIFT * 2,
} as const;

/* ------------------------------------------------------------------ *
 * Amplitudes
 * ------------------------------------------------------------------ */

/** Rotations, in radians. Small. Cards are not pinwheels. */
export const TILT = {
  /** Counter-rotation during the wind-up. */
  anticipate: 0.05,
  /** In-flight lean. */
  flight: 0.12,
  /** Resting tilt of a card in a pile — hand-dealt, not machine-stacked. */
  land: 0.036,
  /** Perspective warp applied as skew while a card is off the felt. */
  skew: 0.055,
  /** Yaw the arriving deck carries as it slides onto the table. */
  entry: 0.14,
  /** Scatter of a deck that has landed but has not been squared yet. Bigger
   *  than `land`, because the whole point of squaring is that you can see it. */
  loose: 0.07,
  /** Leaving the table. */
  exit: 0.42,
  /** A busted card falling away. */
  tumble: 0.95,
  /** Screen-shake roll. */
  shake: 0.012,
} as const;

/** Distances, derived from the card so they scale with the art. */
export const SPAN = {
  /** How far a thrown card bows above the straight line between its ends. */
  arc: CARD.height * 0.34,
  /** Shorter bow, for the undo return and other quiet moves. */
  arcLow: CARD.height * 0.16,
  /** How far busted cards scatter from the pile centre. */
  scatter: CARD.width * 0.38,
  /** The sag a busted card takes on its way off the table. */
  sag: CARD.height * 0.22,
  /** Lateral kick of a riffled card. */
  riffle: CARD.width * 0.13,
  /** How far the two halves of the deck part before they are riffled together. */
  split: CARD.width * 0.4,
  /** How far off true a card lands before the dealer squares the deck. */
  loose: CARD.width * 0.075,
  /** Rejection shudder amplitude. */
  reject: CARD.width * 0.045,
  /** Smallest screen shake. */
  shakeMin: CARD.width * 0.02,
  /** Largest screen shake. */
  shakeMax: CARD.width * 0.075,
} as const;

/** Scale deltas. Applied as `1 + amp(delta)` so reduced motion flattens them. */
export const POP = {
  /** Wind-up swell. */
  ready: 0.03,
  /** Mid-flight — reads as "closer to the eye". */
  flight: 0.07,
  /** Landing squash before the settle. Applied non-uniformly — see the note there. */
  squash: 0.1,
  /** A resolved pile held up. */
  hero: 0.14,
  /** Cards being drawn into the wild — they shrink as they are swallowed. */
  absorb: -0.14,
  /** Pile well under the cursor. */
  hover: 0.035,
  /** Pile well pressed. */
  press: -0.055,
  /** Shrink as something leaves. */
  exit: -0.16,
} as const;

/** Shake oscillation rate, rad/s. Constant across intensities: a big shake is
 *  a *wider* shake, never a slower one. Slow shake reads as a broken renderer. */
/**
 * Shake oscillation rate, radians per second.
 *
 * Raised from 34 when the phase was decoupled from the decay. Under the old
 * eased-proxy drive the *instantaneous* frequency started at 2x this and fell
 * to zero, so the nominal value was never what you heard; at constant pitch 34
 * was audibly too slow to read as an impact.
 */
export const SHAKE_FREQ = 46;

/* ------------------------------------------------------------------ *
 * Reduced motion
 * ------------------------------------------------------------------ */

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

let override: boolean | null = null;
const listeners = new Set<(reduced: boolean) => void>();
let media: MediaQueryList | null = null;

function mediaList(): MediaQueryList | null {
  if (media) return media;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  media = window.matchMedia(REDUCE_QUERY);
  media.addEventListener('change', () => {
    const value = prefersReducedMotion();
    for (const fn of listeners) fn(value);
  });
  return media;
}

/** True when the player has asked for less movement, or an override says so. */
export function prefersReducedMotion(): boolean {
  if (override !== null) return override;
  return mediaList()?.matches ?? false;
}

/**
 * Force reduced motion on or off, e.g. from an in-game accessibility toggle.
 * Pass `null` to hand control back to the media query.
 */
export function setReducedMotion(value: boolean | null): void {
  if (override === value) return;
  override = value;
  const resolved = prefersReducedMotion();
  for (const fn of listeners) fn(resolved);
}

/** Subscribe to changes. Returns an unsubscribe. */
export function onReducedMotionChange(fn: (reduced: boolean) => void): () => void {
  mediaList();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** A duration, through the reduced-motion filter. */
export function dur(seconds: number): number {
  return prefersReducedMotion() ? seconds * 0.5 : seconds;
}

/** A flourish amplitude, through the reduced-motion filter. */
export function amp(value: number): number {
  return prefersReducedMotion() ? 0 : value;
}

/** An ease name, through the reduced-motion filter. */
export function ease(name: EaseName): EaseName {
  if (!prefersReducedMotion()) return name;
  return EXPRESSIVE.test(name) ? EASE.calm : name;
}

/* ------------------------------------------------------------------ *
 * Small maths, shared so nobody rolls their own
 * ------------------------------------------------------------------ */

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Total span of a staggered run — `count` items, `each` apart, each taking
 * `length`. Use it to budget a sequence before you build it.
 */
export function staggerSpan(count: number, each: number, length: number): number {
  return Math.max(0, count - 1) * each + length;
}

/**
 * Deterministic signed jitter in [-1, 1] from an integer seed. Piles must look
 * hand-dealt, but they must also look *the same* hand-dealt every time a card
 * is re-placed after an undo, so this is a hash, not Math.random.
 */
export function jitter(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return (x - Math.floor(x)) * 2 - 1;
}
